import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Customer } from '../../types';
import { Search, Building2, ChevronDown, Check, Plus } from 'lucide-react';

interface Props {
  customers: Customer[];
  value?: string;
  onSelect: (c: Customer) => void;
  placeholder?: string;
  /**
   * Fusszeile "Neuen Kunden anlegen" — bekommt den Suchbegriff als
   * Vorbelegung. Fehlt der Handler, gibt es die Zeile nicht.
   */
  onCreateNew?: (query: string) => void;
}

// Durchsuchbares Kunden-Dropdown (Combobox). Stil an crm/-Komponenten angelehnt.
export const CustomerPicker: React.FC<Props> = ({ customers, value, onSelect, placeholder, onCreateNew }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => customers.find((c) => c.id === value), [customers, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.customerNumber || '').toLowerCase().includes(q) ||
        (c.address || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q)
    );
  }, [customers, query]);

  // Klick ausserhalb schliesst das Dropdown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (c: Customer) => {
    onSelect(c);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-left focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all hover:border-slate-300"
      >
        <Building2 className="w-4 h-4 text-slate-300 shrink-0" />
        <span className={`flex-1 truncate ${selected ? 'font-bold text-slate-800' : 'text-slate-400'}`}>
          {selected ? selected.name : placeholder || 'Kunde auswählen…'}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-scale-in">
          <div className="p-2.5 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Name, Kundennummer oder Adresse…"
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-slate-400">Keine Kunden gefunden</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pick(c)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-blue-50/60 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-slate-800 truncate">{c.name || 'Unbenannt'}</div>
                    {(c.customerNumber || c.address) && (
                      <div className="text-xs text-slate-400 truncate">
                        {[c.customerNumber, c.address].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  {c.id === value && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                </button>
              ))
            )}
          </div>
          {onCreateNew && (
            <button
              type="button"
              onClick={() => {
                const q = query.trim();
                setOpen(false);
                setQuery('');
                onCreateNew(q);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 border-t border-slate-100 text-left text-sm font-bold text-blue-600 hover:bg-blue-50/60 transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Plus className="w-4 h-4" />
              </div>
              <span className="truncate">
                {query.trim() ? `„${query.trim()}“ als neuen Kunden anlegen` : 'Neuen Kunden anlegen'}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
